/**
 * CodeExplorer - VS Code-style left panel for Workspace
 * Shows COBOL structure in tree view
 */

import { useState } from 'react'
import {
  ChevronRight,
  ChevronDown,
  FileCode,
  FolderOpen,
  Folder,
  Circle,
  Database,
  Layout,
  Code2,
  Upload,
  RefreshCw,
  FileText,
  File,
  Inbox,
  Layout as ScreenIcon,
  Archive,
  Server
} from 'lucide-react'
import './CodeExplorer.css'

function CodeExplorer({ analysisData, selectedNodeId, onNodeSelect, onNodeContextMenu, onUpload, onAnalysis }) {
  const [expanded, setExpanded] = useState({
    programs: true,
    copybooks: false,
    jcl: false,
    tables: false,
    queues: false,
    screens: false,
    vsam: false,
    ims: false,
    sequential: false,
    data: false
  })

  const toggleExpand = (key) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }

  const handleNodeClick = (node) => {
    onNodeSelect?.(node)
  }

  const handleContextMenu = (event, node) => {
    event.preventDefault()
    event.stopPropagation()
    if (onNodeContextMenu) {
      onNodeContextMenu(event, node)
    }
  }

  // If no analysis data, show actions
  if (!analysisData) {
    return (
      <div className="code-explorer">
        <div className="explorer-header">
          <span className="explorer-title">WORKSPACE</span>
        </div>

        <div className="explorer-actions">
          <button className="explorer-action-btn" onClick={onUpload}>
            <Upload size={16} />
            <span>Upload COBOL</span>
          </button>

          <button className="explorer-action-btn" onClick={onAnalysis} disabled>
            <RefreshCw size={16} />
            <span>Run Analysis</span>
            <span className="action-hint">(Upload first)</span>
          </button>
        </div>

        <div className="explorer-empty">
          <p>No analysis data loaded</p>
          <p className="explorer-hint">Upload COBOL files to begin</p>
        </div>
      </div>
    )
  }

  const { programs = [], copybooks = [], tables = [], paragraphs = {} } = analysisData

  // ✅ DUPLICATE KEY DETECTION: Check for duplicate component IDs before render
  const allItems = [...programs, ...copybooks, ...tables]
  const idMap = new Map()  // Track first occurrence
  const duplicateKeys = []

  allItems.forEach(item => {
    if (idMap.has(item.id)) {
      // Found duplicate - log both original and duplicate
      const original = idMap.get(item.id)
      duplicateKeys.push({
        duplicateId: item.id,
        original: { name: original.name, type: original.type, path: original.path },
        duplicate: { name: item.name, type: item.type, path: item.path },
      })
    } else {
      idMap.set(item.id, item)
    }
  })

  // Log duplicates with full details (only when duplicates exist)
  if (duplicateKeys.length > 0) {
    console.error(' [CodeExplorer]  DUPLICATE React keys detected:', {
      totalItems: allItems.length,
      uniqueIds: idMap.size,
      duplicateCount: duplicateKeys.length,
      duplicates: duplicateKeys,
    })

    // Also log all items for debugging
    console.error(' [CodeExplorer] All items for inspection:', allItems.map(item => ({
      id: item.id,
      name: item.name,
      type: item.type,
      path: item.path,
      source: item.metadata?.source,
    })))
  }

  // Extract all mainframe object types from programs list
  const actualPrograms = programs.filter(p => !p.type || p.type === 'program')
  const jclFiles = programs.filter(p => p.type === 'jcl')
  const queues = programs.filter(p => p.type === 'queue')
  const screens = programs.filter(p => p.type === 'screen')
  const vsamFiles = programs.filter(p => p.type === 'vsam')
  const imsSegments = programs.filter(p => p.type === 'ims')
  const sequentialFiles = programs.filter(p => p.type === 'sequential')
  const dataFiles = programs.filter(p => p.type === 'data')

  return (
    <div className="code-explorer">
      <div className="explorer-header">
        <span className="explorer-title">WORKSPACE</span>
      </div>

      <div className="explorer-actions">
        <button className="explorer-action-btn" onClick={onUpload}>
          <Upload size={16} />
          <span>Upload COBOL</span>
        </button>

        <button className="explorer-action-btn" onClick={onAnalysis}>
          <RefreshCw size={16} />
          <span>Run Analysis</span>
        </button>
      </div>

      <div className="explorer-tree">
        {/* Programs Section */}
        <div className="tree-section">
          <div className="tree-section-header" onClick={() => toggleExpand('programs')}>
            {expanded.programs ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <FileCode size={16} />
            <span>Programs</span>
            <span className="tree-count">({actualPrograms.length})</span>
          </div>

          {expanded.programs && (
            <div className="tree-section-content">
              {actualPrograms.map(program => (
                <ProgramNode
                  key={program.id}
                  program={program}
                  paragraphs={paragraphs[program.name] || []}
                  selected={selectedNodeId === program.id}
                  onSelect={handleNodeClick}
                  onContextMenu={handleContextMenu}
                />
              ))}
            </div>
          )}
        </div>

        {/* Copybooks Section */}
        <div className="tree-section">
          <div className="tree-section-header" onClick={() => toggleExpand('copybooks')}>
            {expanded.copybooks ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <Layout size={16} />
            <span>Copybooks</span>
            <span className="tree-count">({copybooks.length})</span>
          </div>

          {expanded.copybooks && (
            <div className="tree-section-content">
              {copybooks.map(copybook => (
                <div
                  key={copybook.id}
                  className={`tree-item ${selectedNodeId === copybook.id ? 'selected' : ''}`}
                  onClick={() => handleNodeClick(copybook)}
                  onContextMenu={(e) => handleContextMenu(e, copybook)}
                >
                  <Code2 size={14} />
                  <span>{copybook.name}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* JCL Files Section */}
        {jclFiles.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('jcl')}>
              {expanded.jcl ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <FileText size={16} />
              <span>JCL Files</span>
              <span className="tree-count">({jclFiles.length})</span>
            </div>

            {expanded.jcl && (
              <div className="tree-section-content">
                {jclFiles.map(jcl => (
                  <div
                    key={jcl.id}
                    className={`tree-item ${selectedNodeId === jcl.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(jcl)}
                    onContextMenu={(e) => handleContextMenu(e, jcl)}
                  >
                    <FileText size={14} />
                    <span>{jcl.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Data Files Section */}
        {dataFiles.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('data')}>
              {expanded.data ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <File size={16} />
              <span>Data Files</span>
              <span className="tree-count">({dataFiles.length})</span>
            </div>

            {expanded.data && (
              <div className="tree-section-content">
                {dataFiles.map(dataFile => (
                  <div
                    key={dataFile.id}
                    className={`tree-item ${selectedNodeId === dataFile.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(dataFile)}
                    onContextMenu={(e) => handleContextMenu(e, dataFile)}
                  >
                    <File size={14} />
                    <span>{dataFile.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tables Section */}
        {tables.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('tables')}>
              {expanded.tables ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Database size={16} />
              <span>Tables</span>
              <span className="tree-count">({tables.length})</span>
            </div>

            {expanded.tables && (
              <div className="tree-section-content">
                {tables.map(table => (
                  <div
                    key={table.id}
                    className={`tree-item ${selectedNodeId === table.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(table)}
                    onContextMenu={(e) => handleContextMenu(e, table)}
                  >
                    <Database size={14} />
                    <span>{table.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* MQ Queues Section */}
        {queues.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('queues')}>
              {expanded.queues ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Inbox size={16} />
              <span>Queues</span>
              <span className="tree-count">({queues.length})</span>
            </div>

            {expanded.queues && (
              <div className="tree-section-content">
                {queues.map(queue => (
                  <div
                    key={queue.id}
                    className={`tree-item ${selectedNodeId === queue.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(queue)}
                    onContextMenu={(e) => handleContextMenu(e, queue)}
                  >
                    <Inbox size={14} />
                    <span>{queue.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Screens/Maps Section */}
        {screens.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('screens')}>
              {expanded.screens ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <ScreenIcon size={16} />
              <span>Screens</span>
              <span className="tree-count">({screens.length})</span>
            </div>

            {expanded.screens && (
              <div className="tree-section-content">
                {screens.map(screen => (
                  <div
                    key={screen.id}
                    className={`tree-item ${selectedNodeId === screen.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(screen)}
                    onContextMenu={(e) => handleContextMenu(e, screen)}
                  >
                    <ScreenIcon size={14} />
                    <span>{screen.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* VSAM Files Section */}
        {vsamFiles.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('vsam')}>
              {expanded.vsam ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Archive size={16} />
              <span>VSAM Files</span>
              <span className="tree-count">({vsamFiles.length})</span>
            </div>

            {expanded.vsam && (
              <div className="tree-section-content">
                {vsamFiles.map(vsam => (
                  <div
                    key={vsam.id}
                    className={`tree-item ${selectedNodeId === vsam.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(vsam)}
                    onContextMenu={(e) => handleContextMenu(e, vsam)}
                  >
                    <Archive size={14} />
                    <span>{vsam.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* IMS Segments Section */}
        {imsSegments.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('ims')}>
              {expanded.ims ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <Server size={16} />
              <span>IMS Segments</span>
              <span className="tree-count">({imsSegments.length})</span>
            </div>

            {expanded.ims && (
              <div className="tree-section-content">
                {imsSegments.map(ims => (
                  <div
                    key={ims.id}
                    className={`tree-item ${selectedNodeId === ims.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(ims)}
                    onContextMenu={(e) => handleContextMenu(e, ims)}
                  >
                    <Server size={14} />
                    <span>{ims.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Sequential Files Section */}
        {sequentialFiles.length > 0 && (
          <div className="tree-section">
            <div className="tree-section-header" onClick={() => toggleExpand('sequential')}>
              {expanded.sequential ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              <File size={16} />
              <span>Sequential Files</span>
              <span className="tree-count">({sequentialFiles.length})</span>
            </div>

            {expanded.sequential && (
              <div className="tree-section-content">
                {sequentialFiles.map(seq => (
                  <div
                    key={seq.id}
                    className={`tree-item ${selectedNodeId === seq.id ? 'selected' : ''}`}
                    onClick={() => handleNodeClick(seq)}
                    onContextMenu={(e) => handleContextMenu(e, seq)}
                  >
                    <File size={14} />
                    <span>{seq.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ProgramNode with nested divisions/paragraphs (like VS Code OUTLINE)
function ProgramNode({ program, paragraphs, selected, onSelect, onContextMenu }) {
  const [expanded, setExpanded] = useState(false)
  const [divisionsExpanded, setDivisionsExpanded] = useState({})

  const toggleExpand = () => setExpanded(!expanded)

  const toggleDivision = (division) => {
    setDivisionsExpanded(prev => ({ ...prev, [division]: !prev[division] }))
  }

  // Group paragraphs by division (mock - would come from analysis)
  const divisions = {
    'PROCEDURE DIVISION': paragraphs.filter(p => p.division === 'PROCEDURE') || paragraphs
  }

  return (
    <div className="program-node">
      <div
        className={`tree-item program-item ${selected ? 'selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation()
          onSelect(program)
        }}
        onContextMenu={(e) => {
          if (onContextMenu) {
            onContextMenu(e, program)
          }
        }}
      >
        <span onClick={(e) => { e.stopPropagation(); toggleExpand() }}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <FileCode size={14} />
        <span>{program.name}</span>
      </div>

      {expanded && (
        <div className="tree-nested">
          {/* PROCEDURE DIVISION */}
          {Object.entries(divisions).map(([divisionName, paras]) => (
            <div key={divisionName} className="division-node">
              <div
                className="tree-item division-item"
                onClick={(e) => { e.stopPropagation(); toggleDivision(divisionName) }}
              >
                {divisionsExpanded[divisionName] ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                <Folder size={12} />
                <span>{divisionName}</span>
                <span className="tree-count">({paras.length})</span>
              </div>

              {divisionsExpanded[divisionName] && (
                <div className="tree-nested">
                  {paras.map(para => (
                    <div
                      key={para.id || para.name}
                      className="tree-item paragraph-item"
                      onClick={(e) => { e.stopPropagation(); onSelect(para) }}
                      onContextMenu={(e) => {
                        if (onContextMenu) {
                          onContextMenu(e, para)
                        }
                      }}
                    >
                      <Circle size={8} className="paragraph-bullet" />
                      <span>{para.name}</span>
                      {para.line_start && (
                        <span className="tree-line-number">:{para.line_start}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default CodeExplorer
